import React, {useEffect, useMemo, useRef, useState} from 'react';
import {CommunityBadgeFromCommunity} from '../community-badge';
import {Community} from '../../models/community';

interface CommunityMultiSelectProps {
  communities: Community[];
  disabled?: boolean;
  error?: boolean;
  id?: string;
  onChange: (communityIds: string[]) => void;
  value: string[];
}

const SEARCH_PLACEHOLDER = 'Search communities...';
const EMPTY_SEARCH_MESSAGE = 'No communities found';
const EMPTY_SEARCH_HINT = 'Try another search term or create a new community.';
const NO_COMMUNITIES_MESSAGE = 'No communities available.';

export const CommunityMultiSelect: React.FC<CommunityMultiSelectProps> = ({
  communities,
  disabled = false,
  error = false,
  id,
  onChange,
  value,
}) => {
  const [search, setSearch] = useState('');

  const filteredCommunities = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return communities;
    return communities.filter(
      c =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.description || '').toLowerCase().includes(term) ||
        (c.id || '').toLowerCase().includes(term),
    );
  }, [communities, search]);

  const selectedCommunities = useMemo(
    () => value.map(communityId => communities.find(c => c.id === communityId)).filter(Boolean) as Community[],
    [value, communities],
  );

  const handleToggle = (communityId: string, checked: boolean) => {
    if (checked) {
      onChange(value.includes(communityId) ? value : [...value, communityId]);
    } else {
      onChange(value.filter(id => id !== communityId));
    }
  };

  const handleRemoveToken = (communityId: string) => {
    onChange(value.filter(id => id !== communityId));
  };

  const allFilteredSelected = filteredCommunities.length > 0 && filteredCommunities.every(c => value.includes(c.id));
  const someFilteredSelected = filteredCommunities.some(c => value.includes(c.id));
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
  }, [someFilteredSelected, allFilteredSelected]);

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      const toRemove = new Set(filteredCommunities.map(c => c.id));
      onChange(value.filter(communityId => !toRemove.has(communityId)));
    } else {
      const toAddIds = filteredCommunities.map(c => c.id);
      const combined = Array.from(new Set(value.concat(toAddIds)));
      onChange(combined);
    }
  };

  const borderClass = error ? 'border-red-500' : 'border-gray-300';

  return (
    <div
      id={id}
      className={`border rounded-lg bg-white ${borderClass} focus-within:ring-2 focus-within:ring-blue-200 focus-within:border-blue-500`}
      role="group"
      aria-label="Select communities">
      {value.length > 0 && (
        <div className="p-3 border-b border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-2">
            Selected Communit{value.length === 1 ? 'y' : 'ies'} ({value.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedCommunities.map(community => (
              <span
                key={community.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-sm border border-blue-100">
                <CommunityBadgeFromCommunity community={community} size={20} variant="shield" />
                <span className="max-w-[120px] truncate">{community.name || community.id}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveToken(community.id)}
                  disabled={disabled}
                  className="p-0.5 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
                  aria-label={`Remove ${community.name || community.id}`}>
                  <span className="text-blue-600 font-medium leading-none" aria-hidden>
                    ✕
                  </span>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="p-2 border-b border-gray-100">
        <input
          type="search"
          placeholder={SEARCH_PLACEHOLDER}
          value={search}
          onChange={e => setSearch(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none disabled:bg-gray-50 disabled:opacity-60"
          aria-label="Search communities"
        />
      </div>

      <div className="max-h-56 overflow-y-auto p-2">
        {communities.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">{NO_COMMUNITIES_MESSAGE}</p>
        ) : filteredCommunities.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm font-medium text-gray-700">{EMPTY_SEARCH_MESSAGE}</p>
            <p className="text-xs text-gray-500 mt-1">{EMPTY_SEARCH_HINT}</p>
          </div>
        ) : (
          <>
            {communities.length > 1 && (
              <label className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 focus-within:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={handleSelectAll}
                  disabled={disabled}
                  className="w-4 h-4 flex-shrink-0 text-blue-600 bg-white border-gray-400 rounded focus:ring-2 focus:ring-blue-200 disabled:opacity-50"
                  aria-label="Select all filtered communities"
                />
                <span className="text-sm font-medium text-gray-700">Select All</span>
                {search.trim() && <span className="text-xs text-gray-500">(filtered results)</span>}
              </label>
            )}

            <ul className="space-y-0.5 pt-1">
              {filteredCommunities.map(community => {
                const isSelected = value.includes(community.id);
                return (
                  <li key={community.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      disabled={disabled}
                      onClick={() => handleToggle(community.id, !isSelected)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-left rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-50 ${
                        isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                      }`}>
                      <span
                        className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded border border-gray-400 bg-white text-blue-600 text-xs"
                        aria-hidden>
                        {isSelected ? '✓' : ''}
                      </span>
                      <CommunityBadgeFromCommunity community={community} size={28} variant="shield" />
                      <span className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900 block truncate">{community.displayLabel}</span>
                        {community.description && <span className="text-xs text-gray-500 block truncate">{community.description}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
